import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Sidebar.scss';
import SidebarProfileCard from './SidebarProfileCard';
import { useAuth } from '../../contexts/AuthContext';
import { useUnreadCounts } from '../../contexts/UnreadCountsContext';
import { getAccessToken } from '../../awsAuth';
import axios from 'axios';

import homeIcon from './img/home.png';
import homeIconActive from './img/home-active.png';

import addIcon from './img/add.png';
import addIconActive from './img/add-active.png';

import aiIcon from './img/ai.png';
import aiIconActive from './img/ai-active.png';

import savedIcon from './img/saved.png';
import savedIconActive from './img/saved-active.png';

import notificationIcon from './img/notification.png';
import notificationIconActive from './img/notification-active.png';

import loginIcon from './img/login.png';

import searchIcon from '../Header/img/search.png';
import searchIconActive from '../Header/img/search-active.png';

import messageIcon from '../Header/img/message.png';
import messageIconActive from '../Header/img/message-active.png';

const DEFAULT_AVATAR_URL = 'https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/avatar.png';

const COMMUNITIES_UNREAD_POLL_MS = 20000;

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API ||
  (typeof window !== "undefined" &&
  window.location.hostname.includes("onrender.com")
    ? "https://cvanto-api.onrender.com"
    : "http://localhost:5000");

const Sidebar = () => {
  const { userId, isLoggedIn } = useAuth();
  const location = useLocation();

  const [avatarUrl, setAvatarUrl] = useState(DEFAULT_AVATAR_URL);

  const {
    notifUnread,
    messagesUnread,
    markNotificationsReadLocally,
    markMessagesReadLocally,
  } = useUnreadCounts();

  const [communitiesUnreadOn, setCommunitiesUnreadOn] = useState(0);
  const [communitiesUnreadOff, setCommunitiesUnreadOff] = useState(0);

  useEffect(() => {
    try {
      localStorage.removeItem("cvanto_avatar");
    } catch {}
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadAvatar = async () => {
      if (!userId || !isLoggedIn) {
        setAvatarUrl(loginIcon);
        return;
      }

      const avatarCacheKey = `cvanto_avatar_${userId}`;

      try {
        const cachedAvatar = localStorage.getItem(avatarCacheKey);
        if (cachedAvatar) {
          setAvatarUrl(cachedAvatar);
        } else {
          setAvatarUrl(DEFAULT_AVATAR_URL);
        }
      } catch {
        setAvatarUrl(DEFAULT_AVATAR_URL);
      }

      try {
        const token = await getAccessToken?.();

        const res = await axios.get(`${API_BASE}/api/account/${userId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        const nextAvatar =
          res?.data?.avatar && typeof res.data.avatar === "string"
            ? res.data.avatar
            : DEFAULT_AVATAR_URL;

        if (cancelled) return;

        setAvatarUrl(nextAvatar);

        try {
          localStorage.setItem(avatarCacheKey, nextAvatar);
        } catch {}
      } catch {
        if (!cancelled) {
          setAvatarUrl(DEFAULT_AVATAR_URL);
        }
      }
    };

    loadAvatar();

    return () => {
      cancelled = true;
    };
  }, [userId, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn || !userId) {
      setCommunitiesUnreadOn(0);
      setCommunitiesUnreadOff(0);
      return;
    }

    let cancelled = false;
    let inFlight = false;
    let timeoutId = null;

    const loadCommunitiesUnread = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;

      try {
        const token = await getAccessToken();

        if (!token) {
          if (!cancelled) {
            setCommunitiesUnreadOn(0);
            setCommunitiesUnreadOff(0);
          }
          return;
        }

        const [unreadRes, subsRes] = await Promise.all([
          axios.get('/api/communities/unread-bulk', {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }),
          axios.get('/api/communities/subscriptions', {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }).catch(() => ({ data: null }))
        ]);

        const items = Array.isArray(unreadRes.data?.items) ? unreadRes.data.items : [];

        let subs = {};
        const subsData = subsRes.data;

        if (subsData && Array.isArray(subsData.items)) {
          subsData.items.forEach(it => {
            subs[it.communityId] = { enabled: it.enabled !== false };
          });
        }

        let onTotal = 0;
        let offTotal = 0;

        for (const it of items) {
          const unread = Number(it.unread || 0);
          if (!unread) continue;

          const enabled = subs[it.communityId]?.enabled !== false;
          if (enabled) onTotal += unread;
          else offTotal += unread;
        }

        if (!cancelled) {
          setCommunitiesUnreadOn(onTotal);
          setCommunitiesUnreadOff(offTotal);
        }
      } catch (e) {
        if (!cancelled) {
          console.debug('Sidebar: communities unread fetch error', e);
        }
      } finally {
        inFlight = false;
      }
    };

    const startLoop = async () => {
      await loadCommunitiesUnread();
      if (!cancelled) {
        timeoutId = window.setTimeout(startLoop, COMMUNITIES_UNREAD_POLL_MS);
      }
    };

    startLoop();

    const onFocus = () => loadCommunitiesUnread();
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
      window.removeEventListener('focus', onFocus);
    };
  }, [isLoggedIn, userId]);

  useEffect(() => {
    if (!isLoggedIn) return;

    if (location.pathname.startsWith('/notifications')) {
      markNotificationsReadLocally();
    }

    if (location.pathname.startsWith('/messages')) {
      markMessagesReadLocally();
    }

    if (location.pathname.startsWith('/communities')) {
      setCommunitiesUnreadOn(0);
    }
  }, [location.pathname, isLoggedIn, markNotificationsReadLocally, markMessagesReadLocally]);

  const isActive = (path) => location.pathname === path;

  const isMobileProfileActive =
    location.pathname === '/profile' ||
    /^\/profile\/[^/]+/.test(location.pathname);

  const isMessagesOrChat = /^\/messages(\/|$)/.test(location.pathname);
  const isSearchActive = /^\/search(\/|$)/.test(location.pathname);

  const isShowcaseDetails = /^\/showcase\/[^/]+(\/edit)?\/?$/.test(location.pathname);
  // The Cvanto AI chat owns the bottom edge on phones (composer), like an open thread.
  const isChatDetails = /^\/messages\/[^/]+\/?$/.test(location.pathname) || /^\/ai(\/[^/]+)?\/?$/.test(location.pathname);

  const effectiveUnread = location.pathname.startsWith('/notifications') ? 0 : notifUnread;
  const displayUnread = effectiveUnread > 9 ? '9+' : String(effectiveUnread);

  const effectiveMessagesUnread = location.pathname.startsWith('/messages') ? 0 : messagesUnread;
  const displayMessagesUnread = effectiveMessagesUnread > 9 ? '9+' : String(effectiveMessagesUnread);

  const communitiesBadgeRed = location.pathname.startsWith('/communities') ? 0 : communitiesUnreadOn;
  const communitiesBadgeGray = location.pathname.startsWith('/communities') ? 0 : (communitiesUnreadOn ? 0 : communitiesUnreadOff);
  const communitiesBadgeText = (n) => (n > 9 ? '9+' : String(n));

  const handleNotificationsClick = async () => {
    await markNotificationsReadLocally();
  };

  const handleMessagesClick = () => {
    markMessagesReadLocally();
  };

  const handleHomeClick = (e) => {
    if (location.pathname === '/') {
      if (e && typeof e.preventDefault === 'function') {
        e.preventDefault();
      }

      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
  };

  const desktopMenuItems = [
    { key: 'home', label: 'Головна', link: '/', icon: homeIcon, iconActive: homeIconActive },
    { key: 'add', label: 'Додати оголошення', link: '/add-ad', icon: addIcon, iconActive: addIconActive },
    { key: 'notifs', label: 'Сповіщення', link: '/notifications', icon: notificationIcon, iconActive: notificationIconActive, isNotifications: true, onClick: handleNotificationsClick },
    { key: 'ai', label: 'Cvanto AI', link: '/ai', icon: aiIcon, iconActive: aiIconActive },
    { key: 'saved', label: 'Збережені', link: '/saved', icon: savedIcon, iconActive: savedIconActive },
  ];

  const mobileMenuItems = useMemo(() => ([
  { key: 'home', label: 'Головна', link: '/', icon: homeIcon, iconActive: homeIconActive },
  { key: 'messages', label: 'Чати', link: '/messages', icon: messageIcon, iconActive: messageIconActive, isMessages: true },
  { key: 'saved', label: 'Збережені', link: '/saved', icon: savedIcon, iconActive: savedIconActive },
  { key: 'profile', label: 'Профіль', link: isLoggedIn ? '/profile' : '/auth', icon: isLoggedIn ? avatarUrl : loginIcon, isAvatar: isLoggedIn },
]), [isLoggedIn, avatarUrl]);
  
  const getMobileItemActive = (item) => {
    if (item.key === 'home') return location.pathname === '/';
    if (item.key === 'messages') return /^\/messages(\/|$)/.test(location.pathname);
    if (item.key === 'saved') return /^\/saved(\/|$)/.test(location.pathname);
    if (item.key === 'profile') return isMobileProfileActive;
    if (item.key === 'search') return isSearchActive;
    return isActive(item.link);
  };

  const activeIndex = Math.max(0, mobileMenuItems.findIndex(getMobileItemActive));
  const desktopActiveIndex = Math.max(0, desktopMenuItems.findIndex(i => isActive(i.link)));

  return (
    <div className={`sidebar ${(isShowcaseDetails || isChatDetails) ? 'sidebar--hide-mobile' : ''}`}>
      <div className="desktop-only">
        <SidebarProfileCard />
      </div>

      <div className="sidebar__nav-card desktop-only">
      <nav
        className="sidebar__menu sidebar__deskbar"
        aria-label="Ліва панель навігації"
        style={{ '--desk-slots': desktopMenuItems.length, '--desk-index': desktopActiveIndex }}
      >
        <span className="deskbar__slider" aria-hidden />

        {desktopMenuItems.map((item) => {
          const active = isActive(item.link);

          return (
            <Link
              key={item.key}
              to={item.link}
              onClick={(e) => {
                if (item.onClick) item.onClick(e);
                if (item.key === 'home') handleHomeClick(e);
              }}
              className={`sidebar__menu-item ${active ? 'is-active' : ''} ${item.isNotifications ? 'sidebar__menu-item--notifications' : ''} ${item.isCommunities ? 'sidebar__menu-item--communities' : ''}`}
              aria-current={active ? 'page' : undefined}
            >
              {/* Image SEO (Google Images cleanup, 2026-09): decorative —
                  the visible .sidebar__label right below already gives
                  this Link its name/content signal, on every single page
                  of the site. The old `"{label} icon"` alt (e.g. "Головна
                  icon", "Додати оголошення icon") is the literal source of
                  the bad homepage snippet Google was showing. Matches the
                  mobile nav variant below, which already used alt="". */}
              <img
                src={active && item.iconActive ? item.iconActive : item.icon}
                alt=""
                className={`sidebar__icon ${item.key === 'add' ? 'no-invert' : ''}`}
              />

              <span className="sidebar__label">{item.label}</span>

              {item.isNotifications && effectiveUnread > 0 && (
                <span className={`cv-badge ${effectiveUnread > 9 ? 'cv-badge--max' : ''}`}>
                  {displayUnread}
                </span>
              )}

              {item.isCommunities && communitiesBadgeRed > 0 && (
                <span className={`cv-badge ${communitiesBadgeRed > 9 ? 'cv-badge--max' : ''}`}>
                  {communitiesBadgeText(communitiesBadgeRed)}
                </span>
              )}

              {item.isCommunities && communitiesBadgeRed === 0 && communitiesBadgeGray > 0 && (
                <span className={`cv-badge cv-badge--muted ${communitiesBadgeGray > 9 ? 'cv-badge--max' : ''}`}>
                  {communitiesBadgeText(communitiesBadgeGray)}
                </span>
              )}
            </Link>
          );
        })}
      </nav>
      </div>

      <div className="sidebar__legal desktop-only" aria-label="Legal links">
        <Link to="/privacy" className="sidebar__legal-link">Privacy</Link>
        <Link to="/terms" className="sidebar__legal-link">Terms</Link>
      </div>

      <nav
        className="sidebar__menu mobile-only sidebar__glassbar"
        aria-label="Нижня панель навігації"
        style={{ '--slider-index': activeIndex, '--slots': 4 }}
      >
        <span className="glassbar__slider" aria-hidden />

        {mobileMenuItems.map((item) => {
          const active = getMobileItemActive(item);
          const noInvert = item.isAvatar;
          const showMessagesBadge = item.isMessages && effectiveMessagesUnread > 0;

          return (
            <Link
              key={item.key}
              to={item.link}
              className={`sidebar__menu-item ${active ? 'is-active' : ''} ${item.isSearch ? 'sidebar__menu-item--search' : ''}`}
              aria-current={active ? 'page' : undefined}
              onClick={(e) => {
                if (item.key === 'home') handleHomeClick(e);
                if (item.key === 'messages') handleMessagesClick();
              }}
            >
              <span className={item.isSearch ? 'sidebar__search-orb' : 'sidebar__icon-wrap'}>
                <img
                  src={
                    item.isAvatar
                      ? item.icon
                      : active && item.iconActive
                      ? item.iconActive
                      : item.icon
                  }
                  alt=""
                  className={`sidebar__icon ${item.isAvatar ? 'sidebar__avatar' : ''} ${noInvert ? 'no-invert' : ''}`}
                />
              </span>

              {showMessagesBadge && (
                <span className={`cv-badge cv-badge--messages ${effectiveMessagesUnread > 9 ? 'cv-badge--max' : ''}`}>
                  {displayMessagesUnread}
                </span>
              )}

              {!item.isSearch && (
                <span className="sidebar__label sidebar__label--mobile">
                  {item.label}
                </span>
              )}
            </Link>
          );
        })}
            </nav>
    </div>
  );
};

export default Sidebar;